<template>
  <ResponsiveLayout>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <Spinner v-if="loading" />
      <CorporateHeader v-else />
      <BaseBanner
        v-if="showAlreadyUsedBanner"
        variant="info"
        :title="$t('b2b__verification__already_used_error_title')"
        scroll-into-view
      >
        <p
          class="kds-typography-paragraph-medium"
          v-html="$t('b2b__verification__already_used_error_description')"
        />
        <template #actions>
          <BaseButton
            size="small"
            @click="goToAppLogin"
          >
            {{ $t('b2b__verification__already_used_error_cta_open_app') }}
          </BaseButton>

          <GenericSupportModal
            :subject="$t('b2b__support_modal_subject__login_support')"
          />
        </template>
      </BaseBanner>

      <BaseBanner
        v-if="showGenericErrorBanner"
        variant="info"
        :title="$t('b2b__verification__warning_header')"
        scroll-into-view
      >
        <p
          class="kds-typography-paragraph-large"
          v-html="
            $t('b2b__external_portal_verification_error__description', {
              portal: escapeHtml(corporate.externalPortalName),
            })
          "
        />
        <template #actions>
          <BaseButton
            size="small"
            @click="goToEligibilityCheck"
          >
            {{ $t('generic__try_again') }}
          </BaseButton>
          <GenericSupportModal
            :subject="$t('b2b__support_modal_subject__login_support')"
          />
        </template>
      </BaseBanner>
    </div>
    <template #footer><FooterLinks v-if="!isFromApp()" /></template>
  </ResponsiveLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import { escapeHtml } from '@shared/utils/escapeHtml'
import useUsStore from '@us/app/stores/useUsStore.js'
import { useRoute, useRouter } from 'vue-router'

import ROUTE from '@us/app/router/appModule/names'

import BaseBanner from '@shared/components/BaseBanner.vue'
import Spinner from '@shared/components/Spinner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import GenericSupportModal from '@us/app/components/GenericSupportModal.vue'
import Tracker from '@shared/Tracker.js'
import assembleKaiaAppDeepLink from '@shared/helpers/assembleKaiaAppDeepLink.js'
import CorporateHeader from '@shared/components/CorporateHeader.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import { useI18n } from 'petite-vue-i18n'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import HeaderHelp from '@us/app/components/HeaderHelp.vue'
import useSharedStore from '@shared/stores/useSharedStore.js'
import { setPageTitle } from '@shared/utils'

const corporateStore = useCorporateStore()
const usStore = useUsStore()
const sharedStore = useSharedStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const loading = ref(false)
const showAlreadyUsedBanner = ref(false)
const showGenericErrorBanner = ref(false)

const corporate = computed(() => corporateStore.corporate)

const kaiaSubmissionTrackEvent = (success, errorMessage) => {
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.submit_eligibility_verification',
      action: 'submit',
      object_type: 'eligibility_verification',
      screen_name: 'onb_eligibility_verification',
    },
    {
      search_success: success,
      error_message: errorMessage,
      ...sharedStore.utmData,
    },
  )
}

const goToAppLogin = async () => {
  window.location.redirectToDeeplink(
    await assembleKaiaAppDeepLink({
      path: 'goto/screen',
      query: { target: 'login' },
    }),
  )
}

const goToEligibilityCheck = () => {
  router.push({
    name: ROUTE.VERIFY_WITH_ELIGIBILITY_LIST,
  })
}

const redirectToNext = () => {
  router.push({
    name: ROUTE.SIGN_UP,
    params: {
      // redirect to the corporate identified during the eligibility check
      slug: usStore.corporateEligibilityData.corporate_key,
    },
  })
}

const onSubmit = async () => {
  showAlreadyUsedBanner.value = false
  showGenericErrorBanner.value = false

  Tracker.trackButtonSelect('continue', 'onb_eligibility_verification')

  await usStore.checkEligibilityWithToken(
    corporate.value.key,
    sharedStore.initialQuery.eligible_token,
  )
}

const onSuccess = async () => {
  kaiaSubmissionTrackEvent(true, null)
  console.info('eligible token verification attempt succeeded')
  redirectToNext()
}

const onError = (error) => {
  kaiaSubmissionTrackEvent(false, error)
  switch (error.response.data?.error) {
    case 'ALREADY_USED':
      console.info(
        'eligible token verification attempt soft failed -> proposing login',
        error,
      )
      showAlreadyUsedBanner.value = true
      break
    case 'MULTIPLE_MATCHES_BUT_UNIQUE_MATCH_REQUIRED':
    case 'NOT_ELIGIBLE':
      // saml handshake was successful, but we could not find them in the eligibility lists
      // these users will get access anyway, and we try to reconcile them manually
      console.info(
        'eligible token verification attempt soft failed -> go to signup anyway',
        error,
      )
      redirectToNext()
      break
    case 'TOKEN_BLANK':
    case 'TOKEN_EXPIRED':
    case 'TOKEN_INVALID':
    case 'TOKEN_INCOMPLETE':
      // user is not coming through saml handshake (token is blank), or the token not valid
      showGenericErrorBanner.value = true
      console.warn('eligible token verification attempt failed', error)
      break
    default:
      showGenericErrorBanner.value = true
      console.error('eligible token verification attempt failed', error)
      throw error
  }
}

onMounted(async () => {
  setPageTitle(t('b2b__signup__create_account__page_title'))

  if (!sharedStore.initialQuery.eligible_token || route.query.retry) {
    goToEligibilityCheck()
    return
  }

  try {
    loading.value = true
    await onSubmit()
    await onSuccess()
  } catch (e) {
    loading.value = false
    onError(e)
  }
})
</script>
